/**
 * WalletContext - Manages Thingdom Wallet connection state
 *
 * Enables the marketplace to connect to a user's Thingdom Wallet
 * for agentic transactions (addresses, payment methods, sizes, purchases).
 *
 * Integrates with shared-identity for cross-app user recognition.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

import {
  createIdentityPayload,
  mergeIdentities,
  storeIdentity,
  getStoredIdentity,
} from "../utils/sharedIdentity";

import { useBrand } from "./BrandContext";

const WalletContext = createContext(null);

// Wallet API base URL
const WALLET_API_URL = process.env.REACT_APP_WALLET_API_URL || "https://wallet.thingdom.ai/api";
const WALLET_APP_URL = process.env.REACT_APP_WALLET_APP_URL || "https://wallet.thingdom.ai";
const SESSION_ENDPOINT = process.env.REACT_APP_SESSION_ENDPOINT || "https://wallet-session.pentatonic.workers.dev";

// Infer product category from title for thingventory sync
function inferCategory(title) {
  const t = (title || "").toLowerCase();
  if (/shoe|sneaker|trainer|boot|slide|sandal|clog/.test(t)) return "Footwear";
  if (/jacket|shirt|hoodie|pants|shorts|dress|top|tee/.test(t)) return "Clothing";
  return "Accessories";
}

export function WalletProvider({ children }) {
  const { brand } = useBrand();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [walletToken, setWalletToken] = useState(null);
  const [walletProfile, setWalletProfile] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [sizes, setSizes] = useState(null);
  const [error, setError] = useState(null);

  // Check for existing wallet connection on mount
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const storedToken = localStorage.getItem("pentatonic_wallet_token");
        if (storedToken) {
          // Validate token by fetching profile
          const profile = await fetchWalletProfile(storedToken);
          if (profile) {
            setWalletToken(storedToken);
            setWalletProfile(profile);
            setIsConnected(true);
            console.log("[Wallet] Restored connection for:", profile.email);
          } else {
            // Token invalid, clear it
            localStorage.removeItem("pentatonic_wallet_token");
          }
        }
      } catch (err) {
        console.error("[Wallet] Error checking connection:", err);
        localStorage.removeItem("pentatonic_wallet_token");
      } finally {
        setIsLoading(false);
      }
    };
    checkConnection();
  }, []);

  // Fetch wallet profile using token
  const fetchWalletProfile = async (token) => {
    try {
      const response = await fetch(`${WALLET_API_URL}/v1/external/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) return null;
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      return data.success ? data.data : null;
    } catch (err) {
      console.error("[Wallet] Error fetching profile:", err);
      return null;
    }
  };

  // Connect wallet via popup authorization
  const connectWallet = useCallback(async () => {
    setError(null);

    // Open wallet authorization in popup
    const width = 500;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      `${WALLET_APP_URL}/connect?app=pentatonic_marketplace&scopes=profile,addresses,payment_methods,sizes,purchase&redirect=${encodeURIComponent(window.location.origin + '/wallet/callback')}`,
      "Connect Thingdom Wallet",
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
    );

    if (!popup) {
      setError("Popup blocked. Please allow popups for this site.");
      return false;
    }

    // Listen for message from popup
    return new Promise((resolve) => {
      const handleMessage = async (event) => {
        // Verify origin
        if (event.origin !== WALLET_APP_URL && event.origin !== window.location.origin) {
          return;
        }

        if (event.data?.type === "WALLET_CONNECTED") {
          window.removeEventListener("message", handleMessage);
          popup.close();

          const { accessToken } = event.data;
          if (accessToken) {
            // Store token
            localStorage.setItem("pentatonic_wallet_token", accessToken);
            setWalletToken(accessToken);

            // Fetch profile
            const profile = await fetchWalletProfile(accessToken);
            if (profile) {
              setWalletProfile(profile);
              setIsConnected(true);
              console.log("[Wallet] Connected:", profile.email);

              // Create and store shared identity for cross-app recognition
              const existingIdentity = getStoredIdentity();
              const walletIdentity = createIdentityPayload({
                email: profile.email,
                displayName: profile.displayName,
                avatarUrl: profile.avatarUrl,
                source: 'wallet',
                sourceId: profile.userId,
              });
              walletIdentity.wallet_connected = true;

              // Merge with existing marketplace identity if present
              if (existingIdentity && existingIdentity.source === 'marketplace') {
                const mergedIdentity = mergeIdentities(existingIdentity, walletIdentity);
                storeIdentity(mergedIdentity);
                console.log("[Wallet] Linked marketplace identity with wallet");
              } else {
                storeIdentity(walletIdentity);
                console.log("[Wallet] Stored wallet identity");
              }

              resolve(true);
            } else {
              setError("Failed to fetch wallet profile");
              resolve(false);
            }
          } else {
            setError("No access token received");
            resolve(false);
          }
        } else if (event.data?.type === "WALLET_CANCELLED") {
          window.removeEventListener("message", handleMessage);
          popup.close();
          resolve(false);
        }
      };

      window.addEventListener("message", handleMessage);

      // Check if popup was closed without completing
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener("message", handleMessage);
          resolve(false);
        }
      }, 500);
    });
  }, []);

  // Disconnect wallet
  const disconnectWallet = useCallback(() => {
    localStorage.removeItem("pentatonic_wallet_token");
    setWalletToken(null);
    setWalletProfile(null);
    setAddresses([]);
    setPaymentMethods([]);
    setSizes(null);
    setIsConnected(false);

    // Update shared identity to mark wallet as disconnected
    const identity = getStoredIdentity();
    if (identity) {
      identity.wallet_connected = false;
      identity.wallet_address_count = 0;
      identity.wallet_payment_method_count = 0;
      storeIdentity(identity);
    }

    console.log("[Wallet] Disconnected");
  }, []);

  // Fetch addresses from wallet
  const fetchAddresses = useCallback(async () => {
    if (!walletToken) return [];

    try {
      const response = await fetch(`${WALLET_API_URL}/v1/external/addresses`, {
        headers: {
          Authorization: `Bearer ${walletToken}`,
        },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.success) {
        setAddresses(data.data);
        return data.data;
      }
      return [];
    } catch (err) {
      console.error("[Wallet] Error fetching addresses:", err);
      return [];
    }
  }, [walletToken]);

  // Fetch payment methods from wallet
  const fetchPaymentMethods = useCallback(async () => {
    if (!walletToken) return [];

    try {
      const response = await fetch(`${WALLET_API_URL}/v1/external/payment-methods`, {
        headers: {
          Authorization: `Bearer ${walletToken}`,
        },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.success) {
        setPaymentMethods(data.data);
        return data.data;
      }
      return [];
    } catch (err) {
      console.error("[Wallet] Error fetching payment methods:", err);
      return [];
    }
  }, [walletToken]);

  // Fetch sizes from wallet
  const fetchSizes = useCallback(async () => {
    if (!walletToken) return null;

    try {
      const response = await fetch(`${WALLET_API_URL}/v1/external/sizes`, {
        headers: {
          Authorization: `Bearer ${walletToken}`,
        },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.success) {
        setSizes(data.data);
        return data.data;
      }
      return null;
    } catch (err) {
      console.error("[Wallet] Error fetching sizes:", err);
      return null;
    }
  }, [walletToken]);

  // Execute a purchase through the wallet
  const executePurchase = useCallback(async (purchaseData) => {
    if (!walletToken) {
      return { success: false, error: "Wallet not connected" };
    }

    try {
      const response = await fetch(`${WALLET_API_URL}/v1/external/purchase`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${walletToken}`,
        },
        body: JSON.stringify(purchaseData),
      });

      const data = await response.json();

      // Sync purchased item to wallet thingventory (fire-and-forget)
      if (data.success && purchaseData) {
        try {
          const category = inferCategory(purchaseData.productTitle);
          const priceCents = typeof purchaseData.productPrice === "number"
            ? purchaseData.productPrice
            : Math.round(parseFloat(String(purchaseData.productPrice).replace(/[^0-9.]/g, "")) * 100);
          await fetch(`${WALLET_API_URL}/v1/consumer/thingventory`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${walletToken}`,
            },
            body: JSON.stringify({
              brand: purchaseData.productBrand || brand?.name || "Unknown",
              model: purchaseData.productTitle || "Item",
              category,
              estimatedValue: priceCents,
              condition: 10,
              imageUrl: purchaseData.productImage,
            }),
          });
          console.log("[Wallet] Thingventory sync succeeded");
        } catch (syncErr) {
          console.warn("[Wallet] Thingventory sync failed (non-blocking):", syncErr);
        }
      }

      return data;
    } catch (err) {
      console.error("[Wallet] Error executing purchase:", err);
      return { success: false, error: err.message };
    }
  }, [walletToken, brand]);

  // Create a purchase authorization request (requires wallet approval)
  const createPurchaseAuthRequest = useCallback(async (purchaseData) => {
    if (!walletProfile?.userId) {
      return { success: false, error: "Wallet not connected" };
    }

    try {
      const authRequest = {
        walletId: walletProfile.userId,
        merchant: {
          id: brand?.slug || "pentatonic_marketplace",
          name: brand?.name || "Pentatonic Marketplace",
          logoUrl: brand?.branding?.logoUrl
            ? `${window.location.origin}${brand.branding.logoUrl}`
            : "https://marketplace.pentatonic.com/images/logo.svg",
        },
        item: {
          productId: purchaseData.productId,
          title: purchaseData.productTitle,
          imageUrl: purchaseData.productImage,
          priceCents: purchaseData.productPrice,
          currency: purchaseData.productCurrency || "GBP",
        },
        shippingAddress: {
          fullName: purchaseData.address?.recipientName || purchaseData.address?.label,
          lastLine: `${purchaseData.address?.city}, ${purchaseData.address?.postalCode}`,
          full: purchaseData.address,
        },
        paymentMethod: {
          type: purchaseData.paymentMethod?.type || "card",
          brand: purchaseData.paymentMethod?.card?.brand,
          lastFour: purchaseData.paymentMethod?.card?.last4 || "0000",
          expiry: purchaseData.paymentMethod?.card?.expMonth
            ? `${purchaseData.paymentMethod.card.expMonth}/${purchaseData.paymentMethod.card.expYear}`
            : undefined,
        },
        totalCents: purchaseData.productPrice,
        currency: purchaseData.productCurrency || "GBP",
      };

      const response = await fetch(`${SESSION_ENDPOINT}/purchase-auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(authRequest),
      });

      if (!response.ok) {
        console.error("[Wallet] Failed to create purchase auth:", response.status);
        return { success: false, error: "Failed to create purchase request" };
      }

      const data = await response.json();
      console.log("[Wallet] Purchase auth request created:", data.requestId);
      return { success: true, requestId: data.requestId, expiresAt: data.expiresAt };
    } catch (err) {
      console.error("[Wallet] Error creating purchase auth:", err);
      return { success: false, error: err.message };
    }
  }, [walletProfile, brand]);

  // Poll for purchase authorization status
  const getPurchaseAuthStatus = useCallback(async (requestId) => {
    try {
      const response = await fetch(`${SESSION_ENDPOINT}/purchase-auth/${requestId}`);
      if (!response.ok) {
        return { status: "expired" };
      }
      const data = await response.json();
      return { status: data.status };
    } catch (err) {
      console.error("[Wallet] Error getting purchase auth status:", err);
      return { status: "error" };
    }
  }, []);

  // Get default address
  const getDefaultAddress = useCallback(() => {
    return addresses.find(addr => addr.isDefault) || addresses[0] || null;
  }, [addresses]);

  // Get default payment method
  const getDefaultPaymentMethod = useCallback(() => {
    return paymentMethods.find(pm => pm.isDefault) || paymentMethods[0] || null;
  }, [paymentMethods]);

  // Get size for a category
  const getSizeForCategory = useCallback((category) => {
    if (!sizes?.byCategory) return null;
    const categorySize = sizes.byCategory[category];
    return categorySize?.[0]?.size || null;
  }, [sizes]);

  // Get size for a specific brand and category
  const getBrandSize = useCallback((brand, category) => {
    if (!sizes?.byBrand) return null;
    const brandData = sizes.byBrand[brand.toLowerCase()];
    return brandData?.[category]?.size || getSizeForCategory(category);
  }, [sizes, getSizeForCategory]);

  return (
    <WalletContext.Provider
      value={{
        // State
        isConnected,
        isLoading,
        walletToken,
        walletProfile,
        addresses,
        paymentMethods,
        sizes,
        error,

        // Actions
        connectWallet,
        disconnectWallet,
        fetchAddresses,
        fetchPaymentMethods,
        fetchSizes,
        executePurchase,
        createPurchaseAuthRequest,
        getPurchaseAuthStatus,

        // Helpers
        getDefaultAddress,
        getDefaultPaymentMethod,
        getSizeForCategory,
        getBrandSize,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error("useWallet must be used within a WalletProvider");
  }
  return context;
}
